import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, Calendar, Filter, RefreshCw, FileSpreadsheet,
  DollarSign, FileText, Users, Package, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { reportesApi } from '../../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const AGRUPACIONES = [
  { id: 'dia', label: 'Por Día' },
  { id: 'semana', label: 'Por Semana' },
  { id: 'mes', label: 'Por Mes' }
];

export default function VentasPeriodo() {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Filtros - por defecto mes actual
  const hoy = new Date();
  const primerDiaMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const [fechaInicio, setFechaInicio] = useState(primerDiaMes.toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(hoy.toISOString().split('T')[0]);
  const [agrupacion, setAgrupacion] = useState('dia');

  const cargarReporte = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportesApi.getVentasPeriodo({ fechaInicio, fechaFin, agrupacion });
      setData(response.data.data);
    } catch (err) {
      console.error('Error cargando reporte:', err);
      setError(err.response?.data?.error || 'Error al cargar el reporte');
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await reportesApi.exportVentasPeriodo({ fechaInicio, fechaFin, agrupacion });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Ventas_Periodo_${fechaInicio}_${fechaFin}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte exportado');
    } catch (err) {
      console.error('Error exportando:', err);
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    cargarReporte();
  }, []);

  // Formatters
  const formatUSD = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2
  }).format(amount || 0);

  const formatCRC = (amount) => new Intl.NumberFormat('es-CR', {
    style: 'currency', currency: 'CRC', minimumFractionDigits: 0
  }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CR', { day: '2-digit', month: 'short' });
  };

  const formatPeriodo = (item, agrup) => {
    if (agrup === 'mes') {
      const [year, month] = item.periodo.split('-');
      const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      return `${meses[parseInt(month) - 1]} ${year}`;
    }
    if (agrup === 'semana') {
      // Mostrar "Sem X (fecha)"
      const fechaIni = item.fechaInicio ? new Date(item.fechaInicio).toLocaleDateString('es-CR', { day: '2-digit', month: 'short' }) : '';
      return `${item.periodo} (${fechaIni})`;
    }
    // Para día, el periodo ya viene formateado como dd/mm
    return item.periodo;
  };

  // Calcular máximo para el gráfico
  const maxValorGrafico = useMemo(() => {
    if (!data) return 100;
    const maxUSD = data.serieUSD?.reduce((max, d) => Math.max(max, d.total), 0) || 0;
    const maxCRC = data.serieCRC?.reduce((max, d) => Math.max(max, d.total), 0) || 0;
    return Math.max(maxUSD, maxCRC, 100);
  }, [data]);

  // Renderizar indicador de variación
  const renderVariacion = (variacion) => {
    if (variacion === 0) {
      return <span className="flex items-center text-gray-500 text-sm"><Minus className="h-4 w-4 mr-1" /> 0%</span>;
    }
    if (variacion > 0) {
      return <span className="flex items-center text-green-600 text-sm"><ArrowUpRight className="h-4 w-4" /> +{variacion.toFixed(1)}%</span>;
    }
    return <span className="flex items-center text-red-600 text-sm"><ArrowDownRight className="h-4 w-4" /> {variacion.toFixed(1)}%</span>;
  };

  // Renderizar KPIs por moneda
  const renderResumenMoneda = (resumen, moneda) => {
    if (!resumen) return null;
    const formatFn = moneda === 'USD' ? formatUSD : formatCRC;
    const colorClass = moneda === 'USD' ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200 bg-gray-50/30';
    const badgeClass = moneda === 'USD' ? 'bg-blue-500' : 'bg-gray-500';

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl border p-4 ${colorClass}`}
      >
        <div className="flex items-center justify-between mb-3">
          <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${badgeClass}`}>{moneda}</span>
          {renderVariacion(resumen.variacion)}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Ventas</p>
            <p className="text-xl font-bold text-gray-900">{formatFn(resumen.totalVentas)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Facturas</p>
            <p className="text-xl font-bold text-gray-900">{resumen.totalFacturas}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Ticket Promedio</p>
            <p className="text-xl font-bold text-gray-900">{formatFn(resumen.ticketPromedio)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Período Anterior</p>
            <p className="text-lg font-semibold text-gray-600">{formatFn(resumen.ventasAnterior)}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  // Renderizar gráfico de barras simple
  const renderGrafico = (serie, moneda) => {
    if (!serie || serie.length === 0) return null;
    const formatFn = moneda === 'USD' ? formatUSD : formatCRC;
    const barColor = moneda === 'USD' ? 'bg-blue-500' : 'bg-emerald-500';
    const maxLocal = serie.reduce((max, d) => Math.max(max, d.total), 0) || 1;

    return (
      <div className="space-y-2">
        {serie.map((item, idx) => {
          const pct = (item.total / maxLocal) * 100;
          return (
            <div key={idx} className="flex items-center gap-3">
              <div className="w-16 text-xs text-gray-600 text-right flex-shrink-0">
                {formatPeriodo(item, agrupacion)}
              </div>
              <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden relative">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ delay: idx * 0.05, duration: 0.3 }}
                  className={`h-full ${barColor} rounded`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-700">
                  {formatFn(item.total)}
                </span>
              </div>
              <div className="w-12 text-xs text-gray-500 text-right">{item.facturas} fact.</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Ventas por Período</h1>
          <p className="text-gray-500 mt-1">Análisis de ventas agrupadas con comparativos</p>
        </div>
        <button
          onClick={exportarExcel}
          disabled={exporting || !data}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
            exporting || !data
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'border-green-500 text-green-600 hover:bg-green-50'
          )}
        >
          {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          Excel
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agrupar</label>
            <div className="flex gap-1">
              {AGRUPACIONES.map(ag => (
                <button
                  key={ag.id}
                  onClick={() => setAgrupacion(ag.id)}
                  className={clsx(
                    'px-3 py-2 text-sm rounded-lg transition-colors',
                    agrupacion === ag.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {ag.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={cargarReporte}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
            Consultar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* Resumen por moneda */}
          <div className="space-y-4">
            {renderResumenMoneda(data.resumenUSD, 'USD')}
            {renderResumenMoneda(data.resumenCRC, 'CRC')}
          </div>

          {/* Gráficos de tendencia */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.serieUSD?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm border border-blue-200 p-5"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-500 text-white">USD</span>
                  Tendencia de Ventas
                </h3>
                {renderGrafico(data.serieUSD, 'USD')}
              </motion.div>
            )}
            {data.serieCRC?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-500 text-white">CRC</span>
                  Tendencia de Ventas
                </h3>
                {renderGrafico(data.serieCRC, 'CRC')}
              </motion.div>
            )}
          </div>

          {/* Top Productos y Clientes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Productos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-purple-500" />
                Top Productos
              </h3>
              <div className="space-y-3">
                {data.topProductos?.slice(0, 5).map((p, idx) => {
                  const formatFn = p.moneda === 'USD' ? formatUSD : formatCRC;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.descripcion}</p>
                        <p className="text-xs text-gray-500">{p.cantidad} unidades · {p.facturas} facturas</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatFn(p.total)}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${p.moneda === 'USD' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {p.moneda}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {(!data.topProductos || data.topProductos.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">Sin datos</p>
                )}
              </div>
            </motion.div>

            {/* Top Clientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-500" />
                Top Clientes
              </h3>
              <div className="space-y-3">
                {data.topClientes?.map((c, idx) => {
                  const formatFn = c.moneda === 'USD' ? formatUSD : formatCRC;
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.nombre}</p>
                        <p className="text-xs text-gray-500">{c.identificacion} · {c.facturas} facturas</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatFn(c.total)}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${c.moneda === 'USD' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {c.moneda}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {(!data.topClientes || data.topClientes.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-4">Sin datos</p>
                )}
              </div>
            </motion.div>
          </div>

          {/* Info del período */}
          <div className="text-center text-xs text-gray-400">
            Período: {data.periodo?.inicio} al {data.periodo?.fin} ({data.periodo?.dias} días)
            {' · '}
            Comparado con: {data.periodoAnterior?.inicio} al {data.periodoAnterior?.fin}
          </div>
        </>
      )}

      {!loading && !data && !error && (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Seleccione un rango de fechas y haga clic en Consultar</p>
        </div>
      )}
    </div>
  );
}
